import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import {
    getNotificationTypePreferencesRequest,
    getNotificationTypesRequest,
    NotificationsLoadingKeys,
    updateNotificationTypePreferencesRequest,
    updateNotificationTypeRequest,
    sendNotificationTypePreviewRequest,
} from "../../slices/managementSlices/notificationsSlice";
import { OverlayGrayBackground } from "../../components/OverlayGrayBackground";
import NotificationTypeDrawer from "./NotificationTypeDrawer";
import NotificationInfoIcon from "./NotificationInfoIcon";
import {
    NOTIFICATION_TYPE_CATEGORY_LABELS,
    notificationTypeCategoryLabel,
    notificationTypeStatusLabel,
} from "./notificationTypeConsts";

const TABS = [
    { id: "notifications", label: "Notifications" },
    { id: "channels", label: "Delivery channels" },
];

const CAPTIONS = {
    notifications: "Everything Eadin can notify you about and the shared destinations configured for each row.",
    channels: "Where each channel currently delivers, across every notification.",
};

const columnHeader = "border-b border-border px-3 py-2 font-semibold";

const isOutbound = (type) => type.delivery_kind === "outbound";

const infoLines = (type) => {
    const lines = [type.description];
    if (isOutbound(type)) {
        lines.push("Goes to the supplier, not to Eadin users. Shared routing can add an internal copy without changing what the supplier receives.");
    } else {
        lines.push("Choose shared Slack, email, and Eadin bell destinations for this notification.");
    }
    lines.push(`Emitted by ${type.code}. Its name and description come from that code, so they stay true as the system changes.`);
    return lines;
};

const ChannelSwitch = ({ checked, label, onChange }) => (
    <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={onChange}
        className={`relative inline-flex h-[20px] w-[36px] items-center rounded-full ${checked ? "bg-black" : "bg-gray-200"} focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2`}
    >
        <span
            aria-hidden="true"
            className={`block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${checked ? "translate-x-[18px]" : "translate-x-[2px]"}`}
        />
    </button>
);

const DestinationStatus = ({ enabled, channel, notificationName, onClick }) => (
    <button
        type="button"
        title={`Open ${channel} settings for ${notificationName}`}
        aria-label={`Open ${channel} settings for ${notificationName}`}
        onClick={onClick}
        className={`inline-flex min-w-[38px] items-center justify-center rounded-full px-2 py-1 text-[11px] font-semibold ${enabled
            ? "bg-black text-white"
            : "bg-gray-100 text-gray-500"} hover:opacity-80`}
    >
        {enabled ? "On" : "Off"}
    </button>
);

const slackChannelIdsFor = (type) => (
    Array.isArray(type.slack_channel_ids) && type.slack_channel_ids.length
        ? type.slack_channel_ids
        : (type.slack_channel_id ? [type.slack_channel_id] : [])
);

const hasEmailRecipients = (type) => (
    Array.isArray(type.email_recipients) && type.email_recipients.length > 0
);

const NotificationSettings = () => {
    const dispatch = useDispatch();
    const {
        notificationTypes,
        notificationTypesError,
        notificationTypePreferences,
        loadingKeys: notificationsLoadingKeys,
        loading: notificationsLoading,
    } = useSelector((state) => state.notifications);

    const [activeTab, setActiveTab] = useState("notifications");
    const [search, setSearch] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    const [editingCode, setEditingCode] = useState(null);

    useEffect(() => {
        dispatch(getNotificationTypesRequest());
        dispatch(getNotificationTypePreferencesRequest());
    }, [dispatch]);

    const types = useMemo(
        () => (Array.isArray(notificationTypes) ? notificationTypes : []),
        [notificationTypes]
    );

    const preferences = useMemo(
        () => notificationTypePreferences || {},
        [notificationTypePreferences]
    );

    const categoryOptions = useMemo(() => {
        const labels = new Set([
            ...Object.values(NOTIFICATION_TYPE_CATEGORY_LABELS),
            ...types.map((type) => notificationTypeCategoryLabel(type.category)),
        ]);
        return [...labels].filter(Boolean).sort();
    }, [types]);

    const visibleTypes = useMemo(() => {
        const query = search.trim().toLowerCase();
        return types.filter((type) => {
            const categoryLabel = notificationTypeCategoryLabel(type.category);
            const matchesQuery = !query
                || `${type.name} ${type.description} ${categoryLabel}`.toLowerCase().includes(query);
            const matchesCategory = categoryFilter === "all" || categoryLabel === categoryFilter;
            const matchesStatus = statusFilter === "all" || type.status === statusFilter;
            return matchesQuery && matchesCategory && matchesStatus;
        });
    }, [types, search, categoryFilter, statusFilter]);

    const editingType = useMemo(
        () => types.find((type) => type.code === editingCode) || null,
        [types, editingCode]
    );

    const loadingTypes = notificationsLoading
        && notificationsLoadingKeys.includes(NotificationsLoadingKeys.getNotificationTypes);
    const savingType = notificationsLoading
        && notificationsLoadingKeys.includes(NotificationsLoadingKeys.updateNotificationType);
    const sendingPreview = notificationsLoading
        && notificationsLoadingKeys.includes(NotificationsLoadingKeys.sendNotificationTypePreview);

    const saveType = (code, changes) => {
        const { slack_dm, ...registryChanges } = changes;
        const saves = [];
        if (Object.keys(registryChanges).length > 0) {
            saves.push(dispatch(updateNotificationTypeRequest({ code, changes: registryChanges })));
        }
        if (Object.prototype.hasOwnProperty.call(changes, "slack_dm")) {
            saves.push(dispatch(updateNotificationTypePreferencesRequest({
                preferences: [{ code, slack_dm }],
            })));
        }
        Promise.all(saves).then((actions) => {
            if (actions.every((action) => action.type.endsWith("/fulfilled"))) {
                setEditingCode(null);
            }
        });
    };

    const sendPreview = (code, values) => (
        dispatch(sendNotificationTypePreviewRequest({ code, ...values })).unwrap()
    );

    const openSettings = (type) => setEditingCode(type.code);

    const toggleBell = (type) => saveType(type.code, {
        in_app_enabled: !type.in_app_enabled,
    });

    const toggleStatus = (type) => saveType(type.code, {
        status: type.status === "paused" ? "active" : "paused",
    });

    const channelSummary = useMemo(() => {
        const slackTypes = types.filter((type) => slackChannelIdsFor(type).length > 0);
        const emailTypes = types.filter(hasEmailRecipients);
        const inAppTypes = types.filter((type) => type.in_app_enabled);
        const outboundTypes = types.filter(isOutbound);
        const slackChannels = [...new Set(slackTypes.flatMap(slackChannelIdsFor).filter(Boolean))];
        const recipients = [...new Set(emailTypes.flatMap((type) => type.email_recipients || []))];
        return [
            {
                channel: "Slack",
                used: slackTypes.length,
                destination: slackChannels.length
                    ? slackChannels.join(", ")
                    : "No shared Slack channels configured",
            },
            {
                channel: "Email",
                used: emailTypes.length,
                destination: recipients.length
                    ? recipients.join(", ")
                    : "No shared email recipients configured",
            },
            {
                channel: "Eadin",
                used: inAppTypes.length,
                destination: "The notification bell, for users the notification applies to",
            },
            {
                channel: "Supplier email",
                used: outboundTypes.length,
                destination: "The supplier on the purchase order's own email thread",
            },
        ];
    }, [types]);

    return (
        <main className="relative w-full p-4 sm:p-8">
            {(loadingTypes || savingType) && <OverlayGrayBackground />}

            <section className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-3">
                        <h1 className="whitespace-nowrap text-[20px] font-bold leading-none tracking-tight text-gray-900">
                            Notification settings
                        </h1>
                        <div className="flex items-center gap-1" role="tablist" aria-label="Notification settings views">
                            {TABS.map((tab) => (
                                <button
                                    key={tab.id}
                                    type="button"
                                    role="tab"
                                    aria-selected={activeTab === tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`h-8 rounded-md border px-3 text-[13px] font-medium ${activeTab === tab.id
                                        ? "border-black bg-black text-white hover:bg-gray-800"
                                        : "border-transparent bg-white text-gray-700 hover:bg-gray-100"}`}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <p className="mt-1.5 text-[13px] text-gray-500">{CAPTIONS[activeTab]}</p>
                </div>
            </section>

            {activeTab === "notifications" && (
                <>
                    <section className="mt-7 flex flex-wrap items-center gap-3" aria-label="Notification filters">
                        <label className="min-w-[240px] flex-1 sm:max-w-[410px]">
                            <span className="sr-only">Search notifications</span>
                            <input
                                type="search"
                                value={search}
                                onChange={(event) => setSearch(event.target.value)}
                                placeholder="Search notifications..."
                                className="h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm text-gray-900 shadow-sm outline-none placeholder:text-gray-400 focus:border-gray-400 focus:ring-1 focus:ring-gray-900"
                            />
                        </label>
                        <label>
                            <span className="sr-only">Category</span>
                            <select
                                value={categoryFilter}
                                onChange={(event) => setCategoryFilter(event.target.value)}
                                className="h-9 min-w-[146px] rounded-md border border-gray-200 bg-white px-3 pr-8 text-[13px] font-medium text-gray-700 shadow-sm outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-900"
                            >
                                <option value="all">All categories</option>
                                {categoryOptions.map((label) => (
                                    <option key={label} value={label}>{label}</option>
                                ))}
                            </select>
                        </label>
                        <label>
                            <span className="sr-only">Status</span>
                            <select
                                value={statusFilter}
                                onChange={(event) => setStatusFilter(event.target.value)}
                                className="h-9 min-w-[132px] rounded-md border border-gray-200 bg-white px-3 pr-8 text-[13px] font-medium text-gray-700 shadow-sm outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-900"
                            >
                                <option value="all">All status</option>
                                <option value="active">Active</option>
                                <option value="paused">Paused</option>
                            </select>
                        </label>
                        <p className="ml-auto text-[13px] tabular-nums text-gray-400">
                            {`${visibleTypes.length} notification${visibleTypes.length === 1 ? "" : "s"}`}
                        </p>
                    </section>

                    <section className="mt-4 overflow-hidden rounded-lg border border-border bg-white shadow-sm">
                        <div className="overflow-auto">
                            <table className="w-full min-w-[980px] border-collapse text-sm">
                                <thead className="bg-tableHeader">
                                    <tr className="text-left text-[11px] font-semibold uppercase tracking-widest text-gray-500">
                                        <th className={`${columnHeader} w-[32%] border-b-0`} rowSpan={2}>Notification</th>
                                        <th className={`${columnHeader} w-[11%] border-b-0`} rowSpan={2}>Category</th>
                                        <th className={`${columnHeader} w-[9%] border-l border-border text-center`} rowSpan={2}>Slack</th>
                                        <th className={`${columnHeader} w-[9%] text-center`} rowSpan={2}>Email</th>
                                        <th className={`${columnHeader} w-[9%] text-center`} rowSpan={2}>Eadin bell</th>
                                        <th className={`${columnHeader} w-[9%] border-b-0 border-l border-border`} rowSpan={2}>Status</th>
                                        <th className={`${columnHeader} w-[8%] border-b-0 text-right`} rowSpan={2}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleTypes.map((type) => {
                                        return (
                                            <tr key={type.code} className="group border-y border-transparent hover:border-border">
                                                <td className="px-3 py-2.5 align-middle">
                                                    <div className="flex items-start gap-1.5">
                                                        <button
                                                            type="button"
                                                            onClick={() => setEditingCode(type.code)}
                                                            className="block min-w-0 flex-1 text-left"
                                                        >
                                                            <span className="block font-medium text-gray-900 group-hover:underline group-hover:decoration-gray-300 group-hover:underline-offset-4">
                                                                {type.name}
                                                            </span>
                                                            <span className="mt-0.5 block text-xs text-gray-500">
                                                                {type.description}
                                                            </span>
                                                        </button>
                                                        <span className="mt-0.5 shrink-0">
                                                            <NotificationInfoIcon
                                                                label={type.name}
                                                                lines={infoLines(type)}
                                                            />
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-3 py-2.5 align-middle">
                                                    <span className="inline-flex rounded-sm bg-gray-100 px-2 py-1 text-[11px] font-medium leading-none text-gray-600">
                                                        {notificationTypeCategoryLabel(type.category)}
                                                    </span>
                                                </td>
                                                <td className="border-l border-border px-3 py-2.5 text-center align-middle">
                                                    <DestinationStatus
                                                        enabled={slackChannelIdsFor(type).length > 0}
                                                        channel="Slack"
                                                        notificationName={type.name}
                                                        onClick={() => openSettings(type)}
                                                    />
                                                </td>
                                                <td className="px-3 py-2.5 text-center align-middle">
                                                    <DestinationStatus
                                                        enabled={hasEmailRecipients(type)}
                                                        channel="email"
                                                        notificationName={type.name}
                                                        onClick={() => openSettings(type)}
                                                    />
                                                </td>
                                                <td className="px-3 py-2.5 text-center align-middle">
                                                    <ChannelSwitch
                                                        checked={type.in_app_enabled === true}
                                                        label={`Eadin bell delivery for ${type.name}`}
                                                        onChange={() => toggleBell(type)}
                                                    />
                                                </td>
                                                <td className="border-l border-border px-3 py-2.5 align-middle">
                                                    <span className={`inline-flex rounded-sm px-2 py-1 text-[11px] font-medium leading-none ${type.status === "active" ? "bg-emerald-50 text-emerald-800" : "bg-gray-100 text-gray-500"}`}>
                                                        {notificationTypeStatusLabel(type.status)}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-2.5 text-right align-middle">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => setEditingCode(type.code)}
                                                            className="rounded-md border border-gray-200 bg-white px-2 py-1 text-[13px] font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                                                        >
                                                            Edit
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => toggleStatus(type)}
                                                            className="rounded-md px-2 py-1 text-[13px] font-medium text-gray-700 hover:bg-gray-100"
                                                        >
                                                            {type.status === "active" ? "Pause" : "Activate"}
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            {!loadingTypes && visibleTypes.length === 0 && (
                                <div className="flex min-h-[260px] items-center justify-center px-4 text-center text-sm text-gray-500">
                                    {notificationTypesError
                                        ? "Couldn't load notification settings. Try again, or check the API."
                                        : types.length === 0
                                            ? "Notification settings aren't deployed to this environment yet — the notification_types migration hasn't run."
                                            : "No notifications match the selected filters."}
                                </div>
                            )}
                        </div>
                    </section>
                </>
            )}

            {activeTab === "channels" && (
                <section className="mt-4 overflow-hidden rounded-lg border border-border bg-white shadow-sm">
                    <div className="overflow-auto">
                        <table className="w-full min-w-[760px] border-collapse text-sm">
                            <thead className="bg-tableHeader">
                                <tr className="text-left text-[11px] font-semibold uppercase tracking-widest text-gray-500">
                                    <th className={columnHeader}>Channel</th>
                                    <th className={columnHeader}>Used by</th>
                                    <th className={columnHeader}>Destination</th>
                                </tr>
                            </thead>
                            <tbody>
                                {types.length === 0 ? (
                                    <tr>
                                        <td colSpan={3} className="px-3 py-8 text-center text-sm text-gray-500">
                                            {!loadingTypes && "Notification settings aren't deployed to this environment yet — the notification_types migration hasn't run."}
                                        </td>
                                    </tr>
                                ) : channelSummary.map((row) => (
                                    <tr key={row.channel} className="border-y border-transparent hover:border-border">
                                        <td className="px-3 py-3 font-medium text-gray-900">{row.channel}</td>
                                        <td className="px-3 py-3 tabular-nums text-gray-600">
                                            {`${row.used} notification${row.used === 1 ? "" : "s"}`}
                                        </td>
                                        <td className="px-3 py-3 text-gray-600">{row.destination}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            {editingType && (
                <NotificationTypeDrawer
                    type={editingType}
                    preference={preferences[editingType.code] || {}}
                    saving={savingType}
                    sendingPreview={sendingPreview}
                    onClose={() => setEditingCode(null)}
                    onSave={(changes) => saveType(editingType.code, changes)}
                    onSendPreview={(values) => sendPreview(editingType.code, values)}
                />
            )}
        </main>
    );
};

export default NotificationSettings;
